import { useEffect, useRef, useCallback } from 'react';

const SESSION_TIMEOUT_MS = 15 * 60 * 1000;  // 15 minutes
const WARNING_BEFORE_MS  = 1 * 60 * 1000;  // warn 1 minute before expiry

/**
 * useSessionTimeout
 *
 * Monitors user activity (mouse, keyboard, touch, scroll).
 * After 15 minutes of inactivity, silently calls onExpire().
 * Only active when `isAuthenticated` is true.
 */
const useSessionTimeout = ({ isAuthenticated, onWarn, onExpire }) => {
  const timerRef    = useRef(null);
  const warnTimerRef = useRef(null);

  const clearTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warnTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;
    clearTimer();
    // Warning toast 1 minute before expiry
    warnTimerRef.current = setTimeout(() => {
      if (onWarn) onWarn();
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);
    // Auto-logout at 15 minutes
    timerRef.current = setTimeout(() => {
      onExpire();
    }, SESSION_TIMEOUT_MS);
  }, [isAuthenticated, onWarn, onExpire, clearTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimer();
      return;
    }

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

    let ticking = false;
    const handleActivity = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          resetTimer();
          ticking = false;
        });
      }
    };

    EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    resetTimer(); // start timer on mount / login

    return () => {
      EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      clearTimer();
    };
  }, [isAuthenticated, resetTimer, clearTimer]);

  return { resetTimer };
};

export default useSessionTimeout;
